import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Spinner, Alert, Pagination, Modal, Form, Row, Col, Badge } from 'react-bootstrap';
import { FiAlertTriangle, FiRefreshCw, FiEye, FiCheckCircle, FiCalendar, FiCloud, FiTrash2, FiArrowLeft } from 'react-icons/fi';
import { fetchCheckClaimsRequest } from '../../store/actions/claimsActions';
import { deleteCheckClaim } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import WeatherDataSections from '../../components/Common/WeatherDataSections';
import usePersistedSearchState from '../../hooks/usePersistedSearchState';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminClaims.css';

const EMPTY_CHECK_CLAIMS_FILTERS = {
    reservation_number: '',
    policy_code: '',
    client_email: '',
    activity_end_date_from: '',
    activity_end_date_to: '',
    claim_code: '',
    date_of_claim: '',
};

const AdminCheckClaims = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isAuthenticated } = useSelector((state) => state.adminAuth);
    const checkClaimsState = useSelector((state) => state.claims.checkClaims);

    const {
        filters,
        setFilters,
        appliedFilters,
        setAppliedFilters,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        clearSearchState,
    } = usePersistedSearchState('activity-check-claims', EMPTY_CHECK_CLAIMS_FILTERS, {
        persistWhen: isAuthenticated,
    });
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [checkClaimToDelete, setCheckClaimToDelete] = useState(null);
    const [deletingCheckClaimId, setDeletingCheckClaimId] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    const handleDetails = (item) => {
        const checkClaimId = item.check_claim_id || item.forecast_id;
        if (checkClaimId) {
            navigate(`/activity/check-claims/${checkClaimId}`, {
                state: { checkClaim: item }
            });
        } else {
            setSelectedItem(item);
            setShowDetailModal(true);
        }
    };

    const handleOpenDeleteModal = (item) => {
        setCheckClaimToDelete(item);
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setCheckClaimToDelete(null);
    };

    const handleDelete = async () => {
        if (!checkClaimToDelete) {
            return;
        }

        const checkClaimId = checkClaimToDelete.check_claim_id || checkClaimToDelete.forecast_id;
        if (!checkClaimId) {
            setErrorMessage('ID de check claim manquant');
            handleCloseDeleteModal();
            return;
        }

        try {
            setDeletingCheckClaimId(checkClaimId);
            setErrorMessage(null);
            setSuccessMessage(null);
            handleCloseDeleteModal();

            await deleteCheckClaim(checkClaimId);

            const policyCode = checkClaimToDelete.policy_code || checkClaimToDelete.policy_info?.policy_code || '';
            setSuccessMessage(
                `Check claim supprimé avec succès${policyCode ? ` (Police: ${policyCode})` : ''}`
            );
            // Recharger les check claims après un court délai
            setTimeout(() => {
                loadCheckClaims(currentPage);
                setSuccessMessage(null);
            }, 2000);
        } catch (error) {
            let errorMsg = 'Erreur lors de la suppression du check claim';

            // Extraire le message d'erreur du backend
            if (error.response?.data) {
                const responseData = error.response.data;

                // Gérer les erreurs d'intégrité de base de données
                if (error.response.status === 400 && responseData.detail) {
                    const detail = responseData.detail;

                    // Si c'est un objet avec un code d'erreur INTEGRITY_ERROR
                    if (typeof detail === 'object' && detail.code === 'INTEGRITY_ERROR') {
                        // Utiliser le message de résolution en priorité s'il existe
                        if (detail.resolution) {
                            errorMsg = detail.resolution;
                        }
                        // Sinon, utiliser le message du backend
                        else if (detail.message) {
                            errorMsg = detail.message;
                        }
                        // Vérifier les détails d'erreur pour plus d'informations
                        else if (detail.error_details) {
                            const errorDetails = detail.error_details;
                            if (errorDetails.affected_table === 'claims') {
                                errorMsg = 'Impossible de supprimer ce check claim car il est lié à un ou plusieurs sinistres (claims). Veuillez d\'abord supprimer les sinistres associés.';
                            } else {
                                errorMsg = `Impossible de supprimer ce check claim car il est lié à la table "${errorDetails.affected_table}". Veuillez d'abord supprimer les enregistrements associés.`;
                            }
                        } else {
                            errorMsg = 'Impossible de supprimer ce check claim car il est lié à d\'autres données. Veuillez d\'abord supprimer les enregistrements associés.';
                        }
                    }
                    // Si detail est une chaîne
                    else if (typeof detail === 'string') {
                        if (detail.includes('integrity constraint') || detail.includes('violates foreign key') || detail.includes('database constraints')) {
                            errorMsg = 'Impossible de supprimer ce check claim car il est lié à d\'autres données. Veuillez d\'abord supprimer les enregistrements associés.';
                        } else {
                            errorMsg = detail;
                        }
                    }
                    // Si detail est un objet avec un message
                    else if (detail.message) {
                        errorMsg = detail.message;
                    } else {
                        errorMsg = handleApiError(error, errorMsg);
                    }
                } else {
                    errorMsg = handleApiError(error, errorMsg);
                }
            } else if (error.message) {
                errorMsg = error.message;
            } else {
                errorMsg = handleApiError(error, errorMsg);
            }

            setErrorMessage(errorMsg);
            setTimeout(() => setErrorMessage(null), 8000);
        } finally {
            setDeletingCheckClaimId(null);
        }
    };

    // Charger les check claims avec pagination et filtres
    const loadCheckClaims = useCallback((page = 1, filtersToUse = null) => {
        const skip = (page - 1) * itemsPerPage;
        const params = {
            skip,
            limit: itemsPerPage,
        };

        // Utiliser les filtres fournis ou les filtres appliqués
        const activeFilters = filtersToUse !== null ? filtersToUse : appliedFilters;

        // Ajouter les filtres non vides
        Object.keys(activeFilters).forEach((key) => {
            if (activeFilters[key] && activeFilters[key].trim() !== '') {
                params[key] = activeFilters[key].trim();
            }
        });

        dispatch(fetchCheckClaimsRequest(params));
    }, [itemsPerPage, appliedFilters, dispatch]);

    useEffect(() => {
        if (isAuthenticated) {
            loadCheckClaims(currentPage);
        }
        /* eslint-disable-next-line */
    }, [isAuthenticated]);

    useEffect(() => {
        loadCheckClaims(currentPage);
    }, [currentPage, loadCheckClaims]);

    // Extraire les données et métadonnées de la réponse API
    const checkClaimsData = checkClaimsState?.data?.data || checkClaimsState?.data || [];
    const checkClaimsArray = Array.isArray(checkClaimsData) ? checkClaimsData : [];
    const meta = checkClaimsState?.data?.meta || {
        total: 0,
        skip: 0,
        limit: itemsPerPage,
        page: currentPage,
        total_pages: 0,
    };

    // Pagination côté serveur - utiliser les métadonnées de l'API
    const totalPages = meta.total_pages || Math.max(1, Math.ceil(meta.total / itemsPerPage));
    const totalItems = meta.total || 0;
    const indexOfFirstItem = meta.skip || 0;
    const indexOfLastItem = Math.min(indexOfFirstItem + checkClaimsArray.length, totalItems);

    const handleRefresh = () => {
        loadCheckClaims(currentPage);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleApplyFilters = () => {
        setAppliedFilters({ ...filters });
        setCurrentPage(1);
        loadCheckClaims(1, filters);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleApplyFilters();
        }
    };

    const handleClearFilters = () => {
        const emptyFilters = { ...EMPTY_CHECK_CLAIMS_FILTERS };
        clearSearchState();
        setTimeout(() => {
            loadCheckClaims(1, emptyFilters);
        }, 100);
    };

    // Calculate pagination range (show max 5 pages around current)
    const getPaginationRange = () => {
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        return { startPage, endPage };
    };

    // Utiliser directement les check claims de l'API (pas de filtrage côté client)
    const currentCheckClaims = checkClaimsArray;

    const getDeep = (obj, path, fallback = 'N/A') => {
        try {
            const val = path.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
            return val ?? fallback;
        } catch {
            return fallback;
        }
    };

    return (
        <div className="dashboard-page admin-claims-page">
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiCheckCircle className="me-2" />
                                Activities - Check Claims
                            </h2>
                            <p className="text-muted mb-0">Vérification des sinistres d'activités</p>
                        </div>
                        <div>
                            <Button
                                variant="outline-secondary"
                                onClick={() => navigate(-1)}
                                className="d-flex align-items-center"
                            >
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                    </div>

                    {checkClaimsState.error && (
                        <Alert variant="danger" className="mt-3">
                            {checkClaimsState.error}
                        </Alert>
                    )}

                    {successMessage && (
                        <Alert variant="success" className="mt-3" onClose={() => setSuccessMessage(null)} dismissible>
                            <strong>Succès !</strong> {successMessage}
                        </Alert>
                    )}

                    {errorMessage && (
                        <Alert variant="danger" className="mt-3" onClose={() => setErrorMessage(null)} dismissible>
                            <strong>Erreur !</strong> {errorMessage}
                        </Alert>
                    )}

                    {/* Modal de confirmation de suppression */}
                    <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>
                                <FiTrash2 className="me-2" />
                                Confirmer la suppression
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p>Êtes-vous sûr de vouloir supprimer ce check claim ? Cette action est irréversible.</p>
                            {checkClaimToDelete && (
                                <div className="mt-3 p-3 bg-light rounded">
                                    <p className="mb-1"><strong>Code police :</strong> {getDeep(checkClaimToDelete, ['policy_info', 'policy_code'], checkClaimToDelete.policy_code || '—')}</p>
                                    <p className="mb-1"><strong>Numéro réservation :</strong> {getDeep(checkClaimToDelete, ['activity_info', 'reservation_number'], '—')}</p>
                                    <p className="mb-1"><strong>Email client :</strong> {getDeep(checkClaimToDelete, ['client_info', 'email'], '—')}</p>
                                    <p className="mb-0"><strong>ID Check Claim :</strong> {checkClaimToDelete.check_claim_id || checkClaimToDelete.forecast_id || '—'}</p>
                                </div>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseDeleteModal}>
                                Annuler
                            </Button>
                            <Button
                                variant="danger"
                                onClick={handleDelete}
                                disabled={deletingCheckClaimId === (checkClaimToDelete?.check_claim_id || checkClaimToDelete?.forecast_id)}
                            >
                                {deletingCheckClaimId === (checkClaimToDelete?.check_claim_id || checkClaimToDelete?.forecast_id) ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Suppression en cours...
                                    </>
                                ) : (
                                    <>
                                        <FiTrash2 className="me-2" />
                                        Supprimer
                                    </>
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    {/* Filtres avancés */}
                    <Card className="mb-3">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Filtres de recherche</h6>
                            <div className="d-flex gap-2">
                                <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                                    Réinitialiser
                                </Button>
                                <Button variant="primary" size="sm" onClick={handleApplyFilters}>
                                    Appliquer les filtres
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Code police</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="policy_code"
                                            value={filters.policy_code}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Ex: 301826051"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Numéro de réservation</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="reservation_number"
                                            value={filters.reservation_number}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Numéro de réservation"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Email client</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="client_email"
                                            value={filters.client_email}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Email du client"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date fin activité (de)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="activity_end_date_from"
                                            value={filters.activity_end_date_from}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date fin activité (à)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="activity_end_date_to"
                                            value={filters.activity_end_date_to}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Code sinistre</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="claim_code"
                                            value={filters.claim_code}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Code du sinistre"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date du sinistre</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="date_of_claim"
                                            value={filters.date_of_claim}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <div className="d-flex justify-content-end gap-2 mb-3">
                        <Button
                            variant="outline-secondary"
                            onClick={handleRefresh}
                            disabled={checkClaimsState.loading}
                            className="d-flex align-items-center"
                        >
                            <FiRefreshCw className={`me-2 ${checkClaimsState.loading ? 'spinning' : ''}`} />
                            Actualiser
                        </Button>
                    </div>

                    {checkClaimsState.loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Chargement des check claims...</p>
                        </div>
                    ) : checkClaimsState.error ? (
                        <div className="text-center py-5">
                            <Alert variant="danger">{checkClaimsState.error}</Alert>
                        </div>
                    ) : currentCheckClaims.length === 0 ? (
                        <div className="text-center py-5">
                            <FiCheckCircle size={48} className="text-muted mb-3" />
                            <p className="text-muted">Aucun check claim disponible</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <div className="table-scroll">
                                <Table hover striped className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Code police</th>
                                            <th>Numéro réservation</th>
                                            <th>Email client</th>
                                            <th>Forecast déclenché</th>
                                            <th>Forecast vérifié</th>
                                            <th>Observation vérifiée</th>
                                            <th>Trigger Low</th>
                                            <th>Trigger Sup</th>
                                            <th>Date fin activité</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.isArray(currentCheckClaims) && currentCheckClaims.length > 0 ? (
                                            currentCheckClaims.map((item, idx) => (
                                                <tr key={item.check_claim_id || item.forecast_id || idx}>
                                                    <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                        {getDeep(item, ['policy_info', 'policy_code'], 'N/A')}
                                                    </td>
                                                    <td>{getDeep(item, ['activity_info', 'reservation_number'], 'N/A')}</td>
                                                    <td>{getDeep(item, ['client_info', 'email'], 'N/A')}</td>
                                                    <td>
                                                        <Badge bg={getDeep(item, ['is_triggered_forecast']) ? 'success' : 'secondary'}>
                                                            {getDeep(item, ['is_triggered_forecast']) ? 'Oui' : 'Non'}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <Badge bg={getDeep(item, ['forecast_checked']) === 'not created date' ? 'warning' : 'success'}>
                                                            {getDeep(item, ['forecast_checked']) === 'not created date' ? 'Non vérifié' : 'Vérifié'}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        {(() => {
                                                            const isTriggeredObs = getDeep(item, ['is_triggered_observation']);

                                                            // Si is_triggered_observation est null, afficher "En attente de traitement"
                                                            if (isTriggeredObs === null) {
                                                                return (
                                                                    <Badge bg="warning">
                                                                        En attente de traitement
                                                                    </Badge>
                                                                );
                                                            }

                                                            // Si true, afficher "Oui", si false, afficher "Non"
                                                            return (
                                                                <Badge bg={isTriggeredObs === true ? 'success' : 'secondary'}>
                                                                    {isTriggeredObs === true ? 'Oui' : 'Non'}
                                                                </Badge>
                                                            );
                                                        })()}
                                                    </td>
                                                    <td>
                                                        <Badge bg={getDeep(item, ['observation_checked']) === 'not created date' ? 'warning' : 'success'}>
                                                            {getDeep(item, ['observation_checked']) === 'not created date' ? 'Non vérifié' : 'Vérifié'}
                                                        </Badge>
                                                    </td>
                                                    <td>{getDeep(item, ['trigger_low'], 'N/A')}</td>
                                                    <td>{getDeep(item, ['trigger_sup'], 'N/A')}</td>
                                                    <td>{getDeep(item, ['activity_info', 'activity_end_date'], 'N/A')}</td>
                                                    <td>
                                                        <div className="d-flex gap-2">
                                                            <Button
                                                                variant="outline-info"
                                                                size="sm"
                                                                onClick={() => handleDetails(item)}
                                                                title="Détails"
                                                                aria-label="Détails"
                                                            >
                                                                <FiEye />
                                                            </Button>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => handleOpenDeleteModal(item)}
                                                                title="Supprimer"
                                                                aria-label="Supprimer"
                                                                disabled={deletingCheckClaimId === (item.check_claim_id || item.forecast_id)}
                                                            >
                                                                {deletingCheckClaimId === (item.check_claim_id || item.forecast_id) ? (
                                                                    <Spinner animation="border" size="sm" />
                                                                ) : (
                                                                    <FiTrash2 />
                                                                )}
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="10" className="text-center">
                                                    Aucun check claim trouvé
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}

                    {/* Pagination */}
                    {totalItems > 0 && totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-4">
                            <div className="text-muted">
                                Affichage de {indexOfFirstItem + 1} à {indexOfLastItem} sur {totalItems} check claim{totalItems > 1 ? 's' : ''}
                            </div>
                            <Pagination className="mb-0">
                                <Pagination.First
                                    onClick={() => {
                                        setCurrentPage(1);
                                        loadCheckClaims(1);
                                    }}
                                    disabled={currentPage === 1}
                                />
                                <Pagination.Prev
                                    onClick={() => {
                                        const prevPage = currentPage - 1;
                                        setCurrentPage(prevPage);
                                        loadCheckClaims(prevPage);
                                    }}
                                    disabled={currentPage === 1}
                                />
                                {(() => {
                                    const { startPage, endPage } = getPaginationRange();
                                    const pages = [];

                                    // Show first page if not in range
                                    if (startPage > 1) {
                                        pages.push(
                                            <Pagination.Item key={1} onClick={() => {
                                                setCurrentPage(1);
                                                loadCheckClaims(1);
                                            }}>
                                                1
                                            </Pagination.Item>
                                        );
                                        if (startPage > 2) {
                                            pages.push(<Pagination.Ellipsis key="ellipsis-start" />);
                                        }
                                    }

                                    // Show pages in range
                                    for (let i = startPage; i <= endPage; i++) {
                                        pages.push(
                                            <Pagination.Item
                                                key={i}
                                                active={i === currentPage}
                                                onClick={() => {
                                                    setCurrentPage(i);
                                                    loadCheckClaims(i);
                                                }}
                                            >
                                                {i}
                                            </Pagination.Item>
                                        );
                                    }

                                    // Show last page if not in range
                                    if (endPage < totalPages) {
                                        if (endPage < totalPages - 1) {
                                            pages.push(<Pagination.Ellipsis key="ellipsis-end" />);
                                        }
                                        pages.push(
                                            <Pagination.Item key={totalPages} onClick={() => {
                                                setCurrentPage(totalPages);
                                                loadCheckClaims(totalPages);
                                            }}>
                                                {totalPages}
                                            </Pagination.Item>
                                        );
                                    }

                                    return pages;
                                })()}
                                <Pagination.Next
                                    onClick={() => {
                                        const nextPage = currentPage + 1;
                                        setCurrentPage(nextPage);
                                        loadCheckClaims(nextPage);
                                    }}
                                    disabled={currentPage === totalPages}
                                />
                                <Pagination.Last
                                    onClick={() => {
                                        setCurrentPage(totalPages);
                                        loadCheckClaims(totalPages);
                                    }}
                                    disabled={currentPage === totalPages}
                                />
                            </Pagination>
                        </div>
                    )}

                    {/* Modal de détails */}
                    <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
                        <Modal.Header closeButton>
                            <Modal.Title>
                                <FiEye className="me-2" />
                                Détails du check claim
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body style={{ maxHeight: 'calc(90vh - 200px)', overflowY: 'auto' }}>
                            {selectedItem && (
                                <div>
                                    <Card className="mb-3">
                                        <Card.Header className="bg-primary text-white">
                                            <strong>Informations principales</strong>
                                        </Card.Header>
                                        <Card.Body>
                                            <Row>
                                                <Col md={6}>
                                                    <p className="mb-2">
                                                        <strong>Check Claim ID:</strong><br />
                                                        <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                                                            {getDeep(selectedItem, ['check_claim_id'], 'N/A')}
                                                        </span>
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Forecast ID:</strong><br />
                                                        <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                                                            {getDeep(selectedItem, ['forecast_id'], 'N/A')}
                                                        </span>
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Code police:</strong><br />
                                                        {getDeep(selectedItem, ['policy_info', 'policy_code'], 'N/A')}
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Numéro de réservation:</strong><br />
                                                        {getDeep(selectedItem, ['activity_info', 'reservation_number'], 'N/A')}
                                                    </p>
                                                </Col>
                                                <Col md={6}>
                                                    <p className="mb-2">
                                                        <strong><FiCheckCircle className="me-1" />Forecast déclenché:</strong><br />
                                                        <Badge bg={getDeep(selectedItem, ['is_triggered_forecast']) ? 'success' : 'secondary'}>
                                                            {getDeep(selectedItem, ['is_triggered_forecast']) ? 'Oui' : 'Non'}
                                                        </Badge>
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Forecast vérifié:</strong><br />
                                                        <Badge bg={getDeep(selectedItem, ['forecast_checked']) === 'not created date' ? 'warning' : 'success'}>
                                                            {getDeep(selectedItem, ['forecast_checked']) === 'not created date' ? 'Non vérifié' : getDeep(selectedItem, ['forecast_checked'])}
                                                        </Badge>
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Observation vérifiée:</strong><br />
                                                        <Badge bg={getDeep(selectedItem, ['observation_checked']) === 'not created date' ? 'warning' : 'success'}>
                                                            {getDeep(selectedItem, ['observation_checked']) === 'not created date' ? 'Non vérifié' : getDeep(selectedItem, ['observation_checked'])}
                                                        </Badge>
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Dernière modification:</strong><br />
                                                        {getDeep(selectedItem, ['check_claim_last_modified_by'], 'N/A')}
                                                    </p>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>

                                    <Card className="mb-3">
                                        <Card.Header className="bg-info text-white">
                                            <strong>Triggers</strong>
                                        </Card.Header>
                                        <Card.Body>
                                            <Row>
                                                <Col md={3}>
                                                    <p className="mb-2">
                                                        <strong>Trigger Low:</strong><br />
                                                        {getDeep(selectedItem, ['trigger_low'], 'N/A')}
                                                    </p>
                                                </Col>
                                                <Col md={3}>
                                                    <p className="mb-2">
                                                        <strong>Trigger Sup:</strong><br />
                                                        {getDeep(selectedItem, ['trigger_sup'], 'N/A')}
                                                    </p>
                                                </Col>
                                                <Col md={3}>
                                                    <p className="mb-2">
                                                        <strong>Déclenchement cumulé:</strong><br />
                                                        <Badge bg={getDeep(selectedItem, ['prcp_is_cumulated']) ? 'success' : 'secondary'}>
                                                            {getDeep(selectedItem, ['prcp_is_cumulated']) ? 'Oui' : 'Non'}
                                                        </Badge>
                                                    </p>
                                                </Col>
                                                <Col md={3}>
                                                    <p className="mb-2">
                                                        <strong>Heures déclenchées:</strong><br />
                                                        {getDeep(selectedItem, ['count_hour_triggered'], 'N/A')}
                                                    </p>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>

                                    <Card className="mb-3">
                                        <Card.Header className="bg-success text-white">
                                            <strong>Informations d'activité</strong>
                                        </Card.Header>
                                        <Card.Body>
                                            <Row>
                                                <Col md={6}>
                                                    <p className="mb-2">
                                                        <strong>Nom:</strong><br />
                                                        {getDeep(selectedItem, ['activity_info', 'name'], 'N/A')}
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Catégorie:</strong><br />
                                                        {getDeep(selectedItem, ['activity_info', 'category'], 'N/A')}
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Dates:</strong><br />
                                                        Du {getDeep(selectedItem, ['activity_info', 'activity_start_date'], 'N/A')} {getDeep(selectedItem, ['activity_info', 'activity_start_time'], '')}<br />
                                                        Au {getDeep(selectedItem, ['activity_info', 'activity_end_date'], 'N/A')} {getDeep(selectedItem, ['activity_info', 'activity_end_time'], '')}
                                                    </p>
                                                </Col>
                                                <Col md={6}>
                                                    <p className="mb-2">
                                                        <strong>Adresse:</strong><br />
                                                        {getDeep(selectedItem, ['activity_info', 'activity_address'], '')}, {getDeep(selectedItem, ['activity_info', 'activity_zip_code'], '')} {getDeep(selectedItem, ['activity_info', 'activity_city'], '')}, {getDeep(selectedItem, ['activity_info', 'activity_country'], '')}
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Code activité:</strong><br />
                                                        <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                                                            {getDeep(selectedItem, ['activity_info', 'activity_code'], 'N/A')}
                                                        </span>
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Prix:</strong><br />
                                                        {getDeep(selectedItem, ['activity_info', 'activity_price'], 'N/A')}
                                                    </p>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>

                                    <Card className="mb-3">
                                        <Card.Header className="bg-secondary text-white">
                                            <strong>Informations client</strong>
                                        </Card.Header>
                                        <Card.Body>
                                            <Row>
                                                <Col md={6}>
                                                    <p className="mb-2">
                                                        <strong>Nom:</strong><br />
                                                        {getDeep(selectedItem, ['client_info', 'first_name'], '')} {getDeep(selectedItem, ['client_info', 'last_name'], '')}
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Email:</strong><br />
                                                        {getDeep(selectedItem, ['client_info', 'email'], 'N/A')}
                                                    </p>
                                                </Col>
                                                <Col md={6}>
                                                    <p className="mb-2">
                                                        <strong>Code client:</strong><br />
                                                        <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                                                            {getDeep(selectedItem, ['client_info', 'client_code'], 'N/A')}
                                                        </span>
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Téléphone:</strong><br />
                                                        {getDeep(selectedItem, ['client_info', 'phone_number'], 'N/A')}
                                                    </p>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>

                                    {selectedItem?.forecast_info && selectedItem.forecast_info.forecast_id && (
                                        <Card className="mb-3">
                                            <Card.Header className="bg-primary text-white">
                                                <strong>Informations prévision</strong>
                                            </Card.Header>
                                            <Card.Body>
                                                <Row>
                                                    <Col md={6}>
                                                        <p className="mb-2">
                                                            <strong>Source de données:</strong><br />
                                                            {getDeep(selectedItem, ['forecast_info', 'data_source'], 'N/A')}
                                                        </p>
                                                        <p className="mb-2">
                                                            <strong><FiCloud className="me-1" />Total mm précipitation:</strong><br />
                                                            {getDeep(selectedItem, ['forecast_info', 'total_mm_prc'], 'N/A')}
                                                        </p>
                                                    </Col>
                                                    <Col md={6}>
                                                        <p className="mb-2">
                                                            <strong>Date init (Meteomatics):</strong><br />
                                                            {getDeep(selectedItem, ['forecast_info', 'meteomatics_init_day'])}/
                                                            {getDeep(selectedItem, ['forecast_info', 'meteomatics_init_month'])}/
                                                            {getDeep(selectedItem, ['forecast_info', 'meteomatics_init_year'])}
                                                        </p>
                                                        <p className="mb-2">
                                                            <strong>Dernière modification:</strong><br />
                                                            {getDeep(selectedItem, ['forecast_info', 'forecast_last_modified_at'], 'N/A')}
                                                        </p>
                                                    </Col>
                                                </Row>
                                                <WeatherDataSections
                                                    weatherData={selectedItem.forecast_info.weather_data}
                                                    weatherTemp={selectedItem.forecast_info.weather_temp}
                                                    weatherWind={selectedItem.forecast_info.weather_wind}
                                                    enableCopy
                                                    tableSize="sm"
                                                    bordered
                                                    className="mt-3"
                                                    idPrefix="check-claims-modal"
                                                />
                                            </Card.Body>
                                        </Card>
                                    )}

                                    <Card className="mb-0">
                                        <Card.Header className="bg-dark text-white">
                                            <strong>Données complètes (JSON)</strong>
                                        </Card.Header>
                                        <Card.Body>
                                            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.85rem', maxHeight: '300px', overflowY: 'auto' }}>
                                                {JSON.stringify(selectedItem, null, 2)}
                                            </pre>
                                        </Card.Body>
                                    </Card>
                                </div>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
                                Fermer
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminCheckClaims;

